import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import {
  DeviceGroupScheduleModel,
  WeekDayEnum
} from '@models/digital-signage/device-group-schedules.model.ts';
import { TimeRange } from '@models/time-range-picker.model.ts';
import {
  ConflictSourceEnum,
  MinutesRange,
  OccupiedSlot,
  ScheduleDateRange,
  ScheduleOccupancy,
  ScheduleRangeValidation,
  ScheduleRuleFormValue,
  ScheduleRuleValidation,
  ScheduleStatusEnum,
  ScheduleTimeline,
  ScheduleTimeStatusEnum,
  ScheduleTiming
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  SCHEDULE_DAY_PRESETS,
  isLocalSchedule
} from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  SCHEDULE_STATUS_ORDER,
  SCHEDULE_TIME_STATUS_CONFIG
} from '@modules/digital-signage/utils/schedule-appearance.utils.ts';
import {
  formatMinutes,
  formatRange,
  toMinutes
} from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  isDayOutOfRange,
  toDraftRange
} from '@modules/digital-signage/utils/schedule-occupancy.utils.ts';
import {
  formatDateKey,
  getLastDayDate,
  getNextDayDate,
  getScheduleDates,
  isDateInPeriod,
  sortDays,
  toWeekDay
} from '@modules/digital-signage/utils/schedule-date.utils.ts';
import {
  getSavedDayRanges,
  getScheduleSlots,
  isSavedRange
} from '@modules/digital-signage/utils/schedule-slots.utils.ts';

export const getDayTimeline = (
  day: WeekDayEnum,
  ranges: MinutesRange[],
  period: ScheduleDateRange,
  now: Dayjs = dayjs()
): ScheduleTimeline => {
  const date = formatDateKey(now);
  const minutes = toMinutes(now);
  const lastDate = getLastDayDate(day, period);
  const nextDate = getNextDayDate(day, period, now);
  const timeline = (
    status: ScheduleTimeStatusEnum,
    liveUntil: number | null = null
  ): ScheduleTimeline => ({ status, liveUntil, nextDate, lastDate });

  if (period.start && period.start > date) {
    return timeline(ScheduleTimeStatusEnum.Scheduled);
  }
  if (period.end && (lastDate ?? '') < date) {
    return timeline(ScheduleTimeStatusEnum.Ended);
  }
  if (toWeekDay(now) === day && isDateInPeriod(date, period) && ranges.length) {
    const live = ranges.find(
      (range) => range.start <= minutes && minutes < range.end
    );
    if (live) return timeline(ScheduleTimeStatusEnum.Live, live.end);
    if (ranges.some((range) => range.start > minutes)) {
      return timeline(ScheduleTimeStatusEnum.Scheduled);
    }
    return timeline(ScheduleTimeStatusEnum.DoneToday);
  }
  return timeline(ScheduleTimeStatusEnum.Scheduled);
};

const TIME_STATUS_PRIORITY = [
  ScheduleTimeStatusEnum.Live,
  ScheduleTimeStatusEnum.DoneToday,
  ScheduleTimeStatusEnum.Scheduled,
  ScheduleTimeStatusEnum.Ended
];

export const getRangeStatus = (
  days: WeekDayEnum[],
  range: MinutesRange | null,
  period: ScheduleDateRange,
  now: Dayjs = dayjs()
): ScheduleTimeStatusEnum => {
  const statuses = days.map(
    (day) => getDayTimeline(day, range ? [range] : [], period, now).status
  );
  return (
    TIME_STATUS_PRIORITY.find((status) => statuses.includes(status)) ??
    ScheduleTimeStatusEnum.Scheduled
  );
};

export const getScheduleStatus = (
  schedule: ScheduleTiming & Pick<DeviceGroupScheduleModel, '_id'>,
  now: Dayjs = dayjs(),
  savedSlotKeys?: Set<string>
): ScheduleStatusEnum => {
  if (isLocalSchedule(schedule)) return ScheduleStatusEnum.Draft;
  const period = getScheduleDates(schedule);
  const date = formatDateKey(now);
  if (period.start && period.start > date) return ScheduleStatusEnum.Upcoming;
  if (period.end && period.end < date) return ScheduleStatusEnum.Ended;
  const isLive = getScheduleSlots(schedule).some(
    ({ days, range }) =>
      (!savedSlotKeys || isSavedRange(days, range, savedSlotKeys)) &&
      getRangeStatus(days, range, period, now) === ScheduleTimeStatusEnum.Live
  );
  return isLive ? ScheduleStatusEnum.Live : ScheduleStatusEnum.Active;
};

export const sortSchedulesByStatus = <
  T extends { status: ScheduleStatusEnum; index: number }
>(
  items: T[]
): T[] =>
  [...items].sort(
    (a, b) =>
      SCHEDULE_STATUS_ORDER.indexOf(a.status) -
        SCHEDULE_STATUS_ORDER.indexOf(b.status) ||
      (a.status === ScheduleStatusEnum.Draft
        ? b.index - a.index
        : a.index - b.index)
  );

export const getTimelineHint = (
  { status, liveUntil, nextDate, lastDate }: ScheduleTimeline,
  t: TFunction
): string => {
  const formatDay = (date: string) => dayjs(date).format('MMM D');
  if (status === ScheduleTimeStatusEnum.Live && liveUntil !== null) {
    return t('digital_signage_schedule_live_until', {
      time: formatMinutes(liveUntil)
    });
  }
  if (status === ScheduleTimeStatusEnum.DoneToday) {
    return nextDate
      ? t('digital_signage_schedule_done_today_next', {
          date: formatDay(nextDate)
        })
      : t(SCHEDULE_TIME_STATUS_CONFIG[status].label);
  }
  if (status === ScheduleTimeStatusEnum.Ended) {
    return lastDate
      ? t('digital_signage_schedule_ended_on', { date: formatDay(lastDate) })
      : t(SCHEDULE_TIME_STATUS_CONFIG[status].label);
  }
  return '';
};

export const formatDaysText = (days: WeekDayEnum[], t: TFunction): string => {
  const sorted = sortDays(days);
  const preset = Object.entries(SCHEDULE_DAY_PRESETS).find(
    ([, presetDays]) => presetDays.join() === sorted.join()
  );
  if (preset) return t(`digital_signage_schedule_preset_${preset[0]}`);

  const label = (day: WeekDayEnum) => t(`digital_signage_schedule_day_${day}`);
  const isContiguous = sorted.every(
    (day, i) => !i || day === sorted[i - 1] + 1
  );
  return isContiguous && sorted.length > 2
    ? `${label(sorted[0])}–${label(sorted[sorted.length - 1])}`
    : sorted.map(label).join(', ');
};

export const getConflictOwners = (
  conflicts: OccupiedSlot[],
  ruleIndex: number,
  t: TFunction
): string[] => [
  ...new Set(
    conflicts.map((slot) =>
      slot.source === ConflictSourceEnum.Existing
        ? (slot.ownerName ?? '')
        : slot.ruleIndex === ruleIndex
          ? t('digital_signage_schedule_conflict_same_block', {
              time: formatRange(slot, t)
            })
          : t('digital_signage_schedule_rule_title', {
              index: (slot.ruleIndex as number) + 1
            })
    )
  )
];

export const getRuleDayError = (
  validation: ScheduleRuleValidation | undefined,
  isDaysTouched: boolean,
  t: TFunction
): string =>
  validation?.hasNoDays && isDaysTouched
    ? t('digital_signage_schedule_no_days_error')
    : validation?.hasOnlyEndedDays
      ? t('digital_signage_schedule_days_ended_error')
      : validation?.daysOutOfRange.length
        ? t('digital_signage_schedule_days_out_of_range_error', {
            days: formatDaysText(validation.daysOutOfRange, t)
          })
        : '';

export const getRangeErrorMessage = (
  { isPast, isInvalid }: ScheduleRangeValidation,
  { start, end }: TimeRange,
  t: TFunction
): string => {
  if (isPast && !isInvalid)
    return t('digital_signage_schedule_time_past_error');
  if (!isInvalid) return '';
  return start && end && toMinutes(start) === toMinutes(end)
    ? t('digital_signage_schedule_time_same_error')
    : t('digital_signage_schedule_time_invalid_error');
};

export const getRuleRangeStatuses = (
  rule: ScheduleRuleFormValue,
  validation: ScheduleRuleValidation | undefined,
  occupancy: ScheduleOccupancy,
  savedSlotKeys: Set<string>
): (ScheduleTimeStatusEnum | null)[] =>
  rule.timeRanges.map((timeRange, rangeIndex) => {
    const rangeValidation = validation?.ranges[rangeIndex];
    const range = toDraftRange(timeRange);
    if (
      !range ||
      rangeValidation?.isIncomplete ||
      rangeValidation?.isInvalid ||
      rangeValidation?.isPast ||
      rangeValidation?.conflicts.length
    ) {
      return null;
    }
    const inRangeDays = rule.days.filter(
      (day) => !isDayOutOfRange(day, occupancy)
    );
    if (!inRangeDays.length) return null;
    return getRangeStatus(
      inRangeDays,
      isSavedRange(rule.days, range, savedSlotKeys) ? range : null,
      occupancy.period,
      occupancy.now
    );
  });

export const getRuleDayTimeline = (
  day: WeekDayEnum,
  selectedDays: WeekDayEnum[],
  ownRanges: MinutesRange[],
  occupancy: ScheduleOccupancy,
  savedSlotKeys: Set<string>
): ScheduleTimeline | undefined =>
  isDayOutOfRange(day, occupancy)
    ? undefined
    : selectedDays.includes(day)
      ? getDayTimeline(
          day,
          getSavedDayRanges(day, ownRanges, savedSlotKeys),
          occupancy.period,
          occupancy.now
        )
      : occupancy.endedDays.includes(day)
        ? getDayTimeline(day, [], occupancy.period, occupancy.now)
        : undefined;

export const formatDateRange = (
  schedule: Pick<DeviceGroupScheduleModel, 'startDate' | 'endDate'>,
  t: TFunction
): string => {
  const { start, end } = getScheduleDates(schedule);
  const format = (date: string | null) =>
    date ? dayjs(date).format('MMM D, YYYY') : '∞';
  return start || end
    ? `${format(start)} – ${format(end)}`
    : t('digital_signage_schedule_no_date_limit');
};
